import React from 'react';
import { useSelector } from 'react-redux';
import styled, { css } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { isMobileSelector } from 'redux/selectors/global';
import { Typographies, getThemeTypography, Elevation } from 'models';
import Dropzone from 'components/widgets/Dropzone/Dropzone';
import Button from 'components/core/Button/Button';
import Icon, { IconName } from 'components/core/Icon/Icon';

interface IProps {
  gallery: (file: File) => void;
  camera: () => void;
  onClickUploadFromGallery: () => void;
}

const UploadModal = ({ gallery, camera, onClickUploadFromGallery }: IProps) => {
  const { t } = useTranslation();
  const isMobile = useSelector(isMobileSelector);

  const handleDropFile = (files: File[]) => {
    onClickUploadFromGallery();
    if (files?.length) {
      gallery(files[0]);
    }
  };

  const buttonsProps = {
    isMobile
  };

  return (
    <StyledContent>
      <ButtonsWrap {...buttonsProps}>
        <StyledDropzone
          name="upload-from-computer"
          text={isMobile ? t('gallery') : t('upload_from_computer')}
          dropzoneOptions={{
            accept: 'image/*',
            onDrop: handleDropFile
          }}
        />
        <StyledButton name="upload-from-camera" onClick={camera}>
          <StyledButtonContent>
            <StyledIcon name={IconName.CAMERA} />
            <StyledButtonText>{t('take_photo')}</StyledButtonText>
          </StyledButtonContent>
        </StyledButton>
      </ButtonsWrap>
    </StyledContent>
  );
};

export default UploadModal;

const StyledContent = styled.div`
  padding: 2rem 3.5rem 5rem;
`;

const ButtonsWrap = styled.div<{ isMobile: boolean }>`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: flex-start;
  padding: 0;
  gap: 1.6rem;
  width: 100%;
  height: 12.2rem;

  ${({ isMobile }) =>
    isMobile &&
    css`
      margin-left: auto;
    `}
`;

const styledUpload = `
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 2.4prem 1.6rem;
  gap: 0.4rem;
  height: 12.2rem;
`;

const StyledDropzone = styled(Dropzone)`
  ${styledUpload};
  border-radius: 1.2rem;
  border-style: solid;
  line-height: 1.9rem;
  text-align: center;
  text-transform: capitalize;
  ${getThemeTypography(Typographies.BUTTON)};
  background: ${({ theme }) => theme.colors.grey.default};
  border: 0.1rem solid ${({ theme }) => theme.colors.surface4};
  width: ${({ theme }) => (theme.view.isMobile ? '30vw' : '50%')};

  label {
    font-weight: 500;
    padding-left: 0.7rem;
    padding-right: 0.7rem;
    color: ${({ theme }) => theme.colors.surface1};
  }

  span {
    font-size: 3.2rem;
    color: ${({ theme }) => theme.colors.surface1};
  }

  &:hover {
    background: ${({ theme }) => theme.colors.grey.default};
    box-shadow: inset ${({ theme }) => theme.elevation[Elevation.DEPTH6]};
  }

  button {
    display: none;
  }
`;

const StyledButton = styled(Button)`
  ${styledUpload};
  box-shadow: unset;
  border-radius: 1.2rem;
  background: ${({ theme }) => theme.colors.grey.default};
  border: 0.1rem solid ${({ theme }) => theme.colors.surface4};
  width: ${({ theme }) => (theme.view.isMobile ? '30vw' : '50%')};

  &:hover {
    background: ${({ theme }) => theme.colors.grey.default};
    box-shadow: inset ${({ theme }) => theme.elevation[Elevation.DEPTH6]};
  }
`;

const StyledButtonText = styled.span`
  text-align: center;
  text-transform: capitalize;
  color: ${({ theme }) => theme.families.button.primary.main};
  ${({ theme }) => theme.view.isMobile && 'white-space: normal'};
`;

const StyledButtonContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledIcon = styled(Icon)`
  font-size: 3.2rem;
  color: ${({ theme }) => theme.families.button.primary.main};
`;
